import React from 'react';
import { useQuery } from 'react-query';
import studentService from '../../services/api/student';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, 
  Clock, 
  CreditCard, 
  History, 
  ChevronLeft, 
  ShieldCheck, 
  Zap, 
  MessageCircle,
  XCircle,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { getImageUrl } from '../../config/apiConfig';


// Helpers for status display and dates
const getStatusBadge = (status) => {
  switch (status) {
    case 'approved':
      return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    case 'rejected':
      return 'bg-rose-50 text-rose-700 border-rose-100';
    case 'pending':
    default:
      return 'bg-amber-50 text-amber-700 border-amber-100';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'approved': return 'تمت الموافقة';
    case 'rejected': return 'تم الرفض';
    default: return 'قيد المراجعة';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'approved': return <CheckCircle2 size={14} />;
    case 'rejected': return <XCircle size={14} />;
    default: return <Clock size={14} className="animate-pulse" />;
  }
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '—';

export default function PaymentRequests() {
  const navigate = useNavigate();
  // جلب بيانات الطلبات (المنطق الأصلي)
  const { data, isLoading, error } = useQuery(
    'my-payment-requests', 
    () => studentService.getMyPaymentRequests().then(res => res.requests || [])
  );

  const requests = data || [];

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري جلب سجلات الدفع...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-8">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in duration-500">
        <div className="space-y-1">
          <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <History className="text-primary-600 bg-primary-50 p-1.5 rounded-xl" size={36} />
            طلبات الدفع الخاصة بي
          </h1>
          <p className="text-gray-500 font-medium flex items-center gap-2 mr-12">
            <ShieldCheck className="text-emerald-500" size={18} />
            تابع حالة عمليات التحويل والاشتراكات التي قمت بإرسالها للإدارة
          </p>
        </div>
      </div>

      {/* Summary Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 group hover:shadow-md transition-all">
          <div className="p-4 bg-primary-50 text-primary-600 rounded-2xl group-hover:scale-110 transition-transform">
            <CreditCard size={28} />
          </div>
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">إجمالي الطلبات</p>
            <p className="text-3xl font-black text-gray-900">{requests.length}</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 group hover:shadow-md transition-all">
          <div className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl group-hover:scale-110 transition-transform">
            <CheckCircle2 size={28} />
          </div>
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">تم تفعيلها</p>
            <p className="text-3xl font-black text-gray-900">{requests.filter(r => r.status === 'approved').length}</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 group hover:shadow-md transition-all">
          <div className="p-4 bg-amber-50 text-amber-600 rounded-2xl group-hover:scale-110 transition-transform">
            <Clock size={28} />
          </div>
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">قيد الانتظار</p>
            <p className="text-3xl font-black text-gray-900">{requests.filter(r => r.status === 'pending').length}</p>
          </div>
        </div>
      </div>

      {/* Requests Content Area */}
      <div className="space-y-6">
        {requests.length === 0 ? (
          <div className="bg-white rounded-[3rem] p-20 text-center border border-dashed border-gray-200 shadow-sm">
            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <CreditCard size={48} className="text-gray-200" />
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">لا يوجد سجلات دفع</h3>
            <p className="text-gray-500 max-w-sm mx-auto font-medium mb-8">أنت لم تقم بإرسال أي طلبات اشتراك في الكورسات حتى الآن. استكشف المحتوى التعليمي وابدأ رحلتك.</p>
            <button onClick={() => navigate('/student/browse')} className="bg-primary-600 text-white px-10 py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all transform active:scale-95">
                تصفح الكورسات المتاحة
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-50">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-8 py-5 text-right text-xs font-black text-gray-400 uppercase tracking-widest">محتوى الطلب</th>
                  <th className="px-8 py-5 text-right text-xs font-black text-gray-400 uppercase tracking-widest">رقم المعاملة</th>
                  <th className="px-8 py-5 text-right text-xs font-black text-gray-400 uppercase tracking-widest">المبلغ</th>
                  <th className="px-8 py-5 text-right text-xs font-black text-gray-400 uppercase tracking-widest">تاريخ الإرسال</th>
                  <th className="px-8 py-5 text-right text-xs font-black text-gray-400 uppercase tracking-widest">الحالة</th>
                  <th className="px-8 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-50">
                {requests.map((req) => (
                  <tr key={req.id} className="hover:bg-primary-50/30 transition-colors group">
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-4">
                        {req.course_image ? (
                          <img
                            src={getImageUrl(req.course_image) || '/default_course.png'}
                            alt={req.course_title || ''}
                            className="w-12 h-12 rounded-xl object-cover shadow-sm border border-gray-100"
                            onError={(e) => { e.target.onerror = null; e.target.src = '/default_course.png'; }}
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 border border-gray-50">
                            <Zap size={20} />
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-black text-gray-900 group-hover:text-primary-600 transition-colors">{req.course_title || 'كورس غير محدد'}</div>
                          {req.admin_notes && <div className="text-[10px] text-primary-700 font-bold mt-1 italic">ملاحظة: {req.admin_notes}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-sm font-mono text-gray-900">{req.transaction_number || '—'}</td>
                    <td className="px-8 py-6 whitespace-nowrap text-sm font-black text-gray-900">{req.course_price || '—'} <span className="text-[10px] text-gray-400">ج.م</span></td>
                    <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-500">{formatDate(req.created_at)}</td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[10px] font-black border tracking-wider ${getStatusBadge(req.status)}`}>
                        {getStatusIcon(req.status)}
                        {getStatusText(req.status).toUpperCase()}
                      </span>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-left">
                      <div className="flex items-center justify-end gap-3">
                        {req.status === 'approved' && (
                          <button onClick={() => navigate('/student/subscriptions')} className="flex items-center gap-1.5 text-xs font-black text-primary-600 hover:text-primary-700 bg-primary-50 px-4 py-2 rounded-xl transition-all border border-primary-100">
                            عرض الاشتراك <ArrowRight size={14} />
                          </button>
                        )}
                        <button onClick={() => navigate('/contact')} className="p-2 text-gray-400 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100" title="تواصل مع الدعم">
                          <MessageCircle size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer Support Info */}
      <div className="bg-gray-900 rounded-[3rem] p-10 lg:p-16 text-white relative overflow-hidden group shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/20 rounded-full blur-[100px] -z-0"></div>
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-right space-y-4">
                <div className="bg-primary-600 w-14 h-14 rounded-2xl flex items-center justify-center shadow-2xl mb-4 mx-auto md:mr-0 animate-bounce">
                    <Sparkles size={28} />
                </div>
                <h3 className="text-2xl lg:text-3xl font-black leading-tight">هل لديك أي استفسار <br /> حول عملية الدفع؟</h3>
                <p className="text-gray-400 font-medium max-w-md">فريق الدعم الموحد جاهز للرد على استفساراتك وتسجيل اشتراكاتك في أي وقت.</p>
            </div>
            <button onClick={() => navigate('/contact')} className="bg-white text-gray-900 px-12 py-5 rounded-[2rem] font-black text-lg shadow-2xl hover:bg-primary-50 transition-all transform active:scale-95 flex items-center justify-center gap-4">
                تحدث معنا الآن
                <ChevronLeft size={24} />
            </button>
        </div>
      </div>

      {/* Global Error Handling */}
      {error && (
        <div className="bg-rose-50 border border-rose-100 p-6 rounded-3xl flex items-center gap-4 animate-in slide-in-from-bottom duration-500">
            <XCircle className="text-rose-500" size={32} />
            <div>
                <h4 className="font-black text-rose-900">عذراً، حدث خطأ تقني</h4>
                <p className="text-rose-700 text-sm font-medium">لم نتمكن من تحديث سجلات الطلبات، يرجى إعادة تحميل الصفحة.</p>
            </div>
        </div>
      )}
    </div>
  );
}